import apiFetch from '@wordpress/api-fetch';
import {useState, useEffect} from "@wordpress/element";
import {
    Stack, FormControl, InputLabel, InputAdornment,
    Autocomplete, MenuItem, Button, OutlinedInput, TextField
} from '@mui/material';
import {maybeSetParam} from "./util";

function AsyncSelect({field, label, path, val, onChange, onError, hidden = false, fullWidth = false}) {
    const [options, setOptions] = useState([{label: val, value: val}]);

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const values = params.getAll(`${field}[]`);
        console.log(`AsyncSelect: ${field} loaded, query param values: `, values);
        onChange(values);
    }, []);

    useEffect(() => {
        if (hidden === true) return;

        apiFetch({path: path})
            .then((results) => {
                if (val !== null && val !== undefined && !results.includes(val)) {
                    results.unshift(val);
                }
                setOptions(results.map((c) => {
                    if (typeof c === 'string' || c instanceof String) {
                        return {label: c, value: c};
                    }
                    return c;
                }));
            })
            .catch((error) => onError?.(error));
    }, [path, hidden]);

    if (hidden === true) {
        return <></>;
    }
    return (
        <FormControl fullWidth={fullWidth} sx={{minWidth: '125px'}}>
            <Autocomplete
                multiple filterSelectedOptions
                id={field}
                options={options}
                getOptionLabel={(option) => option?.label ?? ''}
                isOptionEqualToValue={(option, value) => {
                    if (Array.isArray(value)) {
                        return value.includes(option.value);
                    }
                    return option.value === value.value;
                }}
                renderInput={(params) => (
                    <TextField {...params} label={label} placeholder={label}/>
                )}
                value={options.filter((option) => val?.includes(option.value))}
                onChange={(_, selected) => {
                    console.log(`AsyncSelect: ${field} changed, selected: `, selected);
                    let newValues = [];
                    for (let i = 0; i < selected.length; i++) {
                        newValues.push(selected[i].value);
                    }
                    let currentUrlParams = new URLSearchParams(window.location.search);
                    maybeSetParam(currentUrlParams, field, newValues);
                    window.history.pushState({}, '', `${window.location.pathname}?${currentUrlParams}`);
                    onChange(newValues);
                }}
            />
        </FormControl>
    );
}

function SqftFilter({min, max, onChange, hidden = false}) {
    const [minSqft, setMinSqft] = useState(min ?? '');
    const [maxSqft, setMaxSqft] = useState(max ?? '');

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const values = [params.get('minSqft'), params.get('maxSqft')];
        console.log(`SqftFilter loaded, query param values: `, values);
        onChange(values);
    }, []);

    const validate = (v) => {
        let result = Number.parseInt(v);
        if (Number.isNaN(result) || result < 0) {
            return 0;
        }
        return result;
    };

    if (hidden === true) {
        return <></>;
    }

    return <Stack direction='row' spacing={2} sx={{minWidth: 350}}>
        <FormControl sx={{m: 1, width: '25ch'}} variant="outlined">
            <InputLabel htmlFor="minSqft" sx={{backgroundColor: '#fff', margin: '5'}}>Min</InputLabel>
            <OutlinedInput
                id="minSqft"
                endAdornment={<InputAdornment position="end">sqft</InputAdornment>}
                aria-describedby="minSqft-helper-text"
                inputProps={{'aria-label': 'Min'}}
                value={minSqft}
                onBlur={() => {
                    if (minSqft > maxSqft) setMaxSqft('');
                }}
                onChange={(e) => setMinSqft(validate(e.target.value))}
                onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                        if (minSqft > maxSqft) setMaxSqft('');
                        onChange([minSqft, maxSqft]);
                    }
                }}
            />
        </FormControl>
        <FormControl sx={{m: 1, width: '25ch'}} variant="outlined">
            <InputLabel htmlFor="maxSqft" sx={{backgroundColor: '#fff', margin: '5'}}>Max</InputLabel>
            <OutlinedInput
                id="maxSqft"
                endAdornment={<InputAdornment position="end">sqft</InputAdornment>}
                aria-describedby="maxSqft-helper-text"
                inputProps={{'aria-label': 'Max'}}
                value={maxSqft}
                onBlur={() => {
                    if (maxSqft > 0 && maxSqft < minSqft) setMinSqft('');
                }}
                onChange={(e) => setMaxSqft(validate(e.target.value))}
                onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                        if (maxSqft > 0 && maxSqft < minSqft) setMinSqft('');
                        onChange([minSqft, maxSqft]);
                    }
                }}
            />
        </FormControl>
        <Button variant='contained' size='medium'
                onClick={() => {
                    let currentUrlParams = new URLSearchParams(window.location.search);
                    maybeSetParam(currentUrlParams, 'minSqft', minSqft);
                    maybeSetParam(currentUrlParams, 'maxSqft', maxSqft);
                    window.history.pushState({}, '', `${window.location.pathname}?${currentUrlParams}`);
                    onChange([minSqft, maxSqft]);
                }}>Apply</Button>
    </Stack>;
}

function RentFilter({min, max, onChange, hidden = false}) {
    const [minRent, setMinRent] = useState(min ?? '');
    const [maxRent, setMaxRent] = useState(max ?? '');

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const values = [params.get('minRent'), params.get('maxRent')];
        console.log(`RentFilter loaded, query param values: `, values);
        onChange(values);
    }, []);

    const validate = (v) => {
        let result = Number.parseInt(v);
        if (Number.isNaN(result) || result < 0) {
            return 0;
        }
        return result;
    };

    if (hidden === true) {
        return <></>;
    }

    return <Stack direction='row' spacing={2} sx={{minWidth: 350}}>
        <FormControl sx={{m: 1, width: '25ch'}} variant="outlined">
            <InputLabel htmlFor="minRent" sx={{backgroundColor: '#fff', margin: '5'}}>Min</InputLabel>
            <OutlinedInput
                id="minRent"
                endAdornment={<InputAdornment position="end">Amt/Month</InputAdornment>}
                aria-describedby="minRent-helper-text"
                inputProps={{'aria-label': 'Min'}}
                value={minRent}
                onBlur={() => {
                    if (minRent > maxRent) setMaxRent('');
                }}
                onChange={(e) => setMinRent(validate(e.target.value))}
                onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                        if (minRent > maxRent) setMaxRent('');
                        onChange([minRent, maxRent]);
                    }
                }}
            />
        </FormControl>
        <FormControl sx={{m: 1, width: '25ch'}} variant="outlined">
            <InputLabel htmlFor="maxRent" sx={{backgroundColor: '#fff', margin: '5'}}>Max</InputLabel>
            <OutlinedInput
                id="maxRent"
                endAdornment={<InputAdornment position="end">Amt/Month</InputAdornment>}
                aria-describedby="maxRent-helper-text"
                inputProps={{'aria-label': 'Max'}}
                value={maxRent}
                onBlur={() => {
                    if (maxRent > 0 && maxRent < minRent) setMinRent('');
                }}
                onChange={(e) => setMaxRent(validate(e.target.value))}
                onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                        if (maxRent > 0 && maxRent < minRent) setMinRent('');
                        onChange([minRent, maxRent]);
                    }
                }}
            />
        </FormControl>
        <Button variant='contained' size='medium'
                onClick={() => {
                    let currentUrlParams = new URLSearchParams(window.location.search);
                    maybeSetParam(currentUrlParams, 'minRent', minRent);
                    maybeSetParam(currentUrlParams, 'maxRent', maxRent);
                    window.history.pushState({}, '', `${window.location.pathname}?${currentUrlParams}`);
                    onChange([minRent, maxRent]);
                }}>Apply</Button>
    </Stack>;
}

export function SpacesControls(
    {
        city,
        onCityChange,
        propertyType,
        onTypeChange,
        subType,
        onSubTypeChange,
        availType,
        onAvailTypeChange,
        minSqft,
        maxSqft,
        onSqftChange,
        minRent,
        maxRent,
        onRentChange,
        showControls = true,
        fullWidth = false,
        onError
    }) {

    useEffect(() => {
        let currentUrlParams = new URLSearchParams(window.location.search);
        maybeSetParam(currentUrlParams, 'city', city);
        maybeSetParam(currentUrlParams, 'propertyType', propertyType);
        maybeSetParam(currentUrlParams, 'subType', subType);
        maybeSetParam(currentUrlParams, 'availType', availType);
        maybeSetParam(currentUrlParams, 'minSqft', minSqft);
        maybeSetParam(currentUrlParams, 'maxSqft', maxSqft);
        maybeSetParam(currentUrlParams, 'minRent', minRent);
        maybeSetParam(currentUrlParams, 'maxRent', maxRent);
        window.history.pushState({}, '', `${window.location.pathname}?${currentUrlParams}`);
    }, [city, propertyType, subType, availType, minSqft, maxSqft, minRent, maxRent]);

    return (
        <Stack direction='column' spacing={2}
               sx={{
                   minWidth: fullWidth ? '100%' : 'auto',
                   justifyContent: fullWidth ? 'space-evenly' : 'center'
               }}>
            <Stack direction='row' spacing={2}
                   sx={{
                       minWidth: fullWidth ? '100%' : 'auto',
                       justifyContent: fullWidth ? 'space-evenly' : 'center'
                   }}>
                <AsyncSelect
                    field='city' label='City' path='/ebo-spaces/v1/cities' val={city}
                    onChange={onCityChange}
                    onError={onError}
                    hidden={!showControls}
                    fullWidth={fullWidth}/>
                <AsyncSelect
                    field='type' label='Property Type' path='/ebo-spaces/v1/types' val={propertyType}
                    onChange={onTypeChange}
                    onError={onError}
                    hidden={!showControls}
                    fullWidth={fullWidth}/>
                <AsyncSelect
                    field='subType' label='Property Sub Type' path='/ebo-spaces/v1/subTypes' val={subType}
                    onChange={onSubTypeChange}
                    onError={onError}
                    hidden={!showControls}
                    fullWidth={fullWidth}/>
                <AsyncSelect
                    field='availType' label='Availability Type' path='/ebo-spaces/v1/availTypes' val={availType}
                    onChange={onAvailTypeChange}
                    onError={onError}
                    hidden={!showControls}
                    fullWidth={fullWidth}/>
            </Stack>
            <Stack direction='row' spacing={2}
                   sx={{
                       minWidth: fullWidth ? '100%' : 'auto',
                       justifyContent: fullWidth ? 'space-evenly' : 'center'
                   }}>
                <SqftFilter min={minSqft} max={maxSqft} onChange={onSqftChange} hidden={!showControls}/>
                <RentFilter min={minRent} max={maxRent} onChange={onRentChange} hidden={!showControls}/>
            </Stack>
        </Stack>
    );
}
